import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { ManagementMediumClippingControllerService, ManagementMediumFilterRequest } from 'api-clipping';
import { SharedService } from '../../../../shared/services/shared.service';
import { UtilsService } from '../../../../shared/services/utils.service';
import { RecordingUploadService } from '../../../../shared/services/recording-upload.service';

interface MediumOption {
	id: string;
	name: string;
}

// Formats RecordingExecutor.isSupportedUploadExtension (backend) accepts.
const ALLOWED_EXTENSIONS = ['mp4', 'ts', 'mkv', 'mov'];
const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

// "Качи снимка" - manual upload of a news edition, for when the recording comes from somewhere
// other than the server's own IPTV capture. Ж1: the actual upload (B2 PUT + finalize) runs in
// RecordingUploadService, a root-provided service - this dialog only validates the form and
// picks the file, then hands off and closes immediately. Progress/errors show in the header icon
// (navbar.component), not here.
@Component({
	selector: 'app-upload-recording-dialog',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatDialogModule,
		MatButtonModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatIconModule,
		MatDividerModule,
		TranslateModule,
	],
	templateUrl: './upload-recording-dialog.component.html',
	styleUrl: './upload-recording-dialog.component.scss',
})
export class UploadRecordingDialogComponent implements OnInit {
	form: FormGroup;
	mediums: MediumOption[] = [];
	hours = HOURS;
	minutes = MINUTES;
	selectedFile: File | null = null;
	fileError: string | null = null;
	// З1 "Прикачи преку линк" - alternative to choosing a file, mutually exclusive with it.
	uploadMode: 'FILE' | 'LINK' = 'FILE';

	constructor(
		private fb: FormBuilder,
		private dialogRef: MatDialogRef<UploadRecordingDialogComponent>,
		private apiMedium: ManagementMediumClippingControllerService,
		private recordingUpload: RecordingUploadService,
		private sharedService: SharedService,
		private utils: UtilsService,
		private translate: TranslateService
	) {
		this.form = this.fb.group({
			mediumId: [null, [Validators.required]],
			scheduledDate: [new Date(), [Validators.required]],
			hour: [null, [Validators.required]],
			minute: [null, [Validators.required]],
			videoLink: ['', [Validators.pattern(/^https?:\/\/.+/i)]],
		});
	}

	setUploadMode(mode: 'FILE' | 'LINK'): void {
		this.uploadMode = mode;
		if (mode === 'FILE') {
			this.form.controls['videoLink'].setValue('');
		} else {
			this.selectedFile = null;
			this.fileError = null;
		}
	}

	ngOnInit(): void {
		this.apiMedium
			.getMediumsPageableFilter({ types: [ManagementMediumFilterRequest.TypesEnum.TV] }, 0, 200)
			.pipe(take(1))
			.subscribe(page => {
				this.mediums = (page.content || [])
					.map(medium => ({
						id: medium.id,
						name: this.utils.getParameterByLanguageScript(medium, 'name') || medium.nameLatin || '',
					}))
					.sort((a, b) => a.name.localeCompare(b.name));
			});
	}

	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files && input.files.length ? input.files[0] : null;
		this.fileError = null;
		this.selectedFile = null;
		if (!file) {
			return;
		}
		const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
		if (!ALLOWED_EXTENSIONS.includes(extension)) {
			this.fileError = this.translate.instant('RECORDINGS.UPLOAD.UNSUPPORTED_FORMAT', { formats: ALLOWED_EXTENSIONS.join(', ') });
			input.value = '';
			return;
		}
		this.selectedFile = file;
	}

	submit(): void {
		if (this.form.invalid) {
			return;
		}
		const mediumId = this.form.value.mediumId;
		const mediumName = this.mediums.find(m => m.id === mediumId)?.name || '';
		const scheduledStart = this.buildScheduledStart();

		if (this.uploadMode === 'FILE') {
			if (!this.selectedFile) {
				return;
			}
			this.recordingUpload.start(mediumId, mediumName, scheduledStart, this.selectedFile);
		} else {
			const link = (this.form.value.videoLink || '').trim();
			if (!link) {
				return;
			}
			this.recordingUpload.startFromLink(mediumId, mediumName, scheduledStart, link);
		}

		this.sharedService.showNotification.next({ message: this.translate.instant('RECORDINGS.UPLOAD.SUCCESS'), type: 'SUCCESS' });
		this.dialogRef.close(true);
	}

	private buildScheduledStart(): string {
		const date: Date = this.form.value.scheduledDate;
		const y = date.getFullYear();
		const m = (date.getMonth() + 1).toString().padStart(2, '0');
		const d = date.getDate().toString().padStart(2, '0');
		return `${y}-${m}-${d}T${this.form.value.hour}:${this.form.value.minute}`;
	}

	cancel(): void {
		this.dialogRef.close();
	}
}
